import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One real voice for the whole platform. Browser speech synthesis is deliberately
 * never used — if the audio can't be produced we stay silent rather than sound robotic.
 */
export function useNarration() {
  const { getAuthHeader } = useAuth();
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);

  const stop = useCallback(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setSpeaking(false);
  }, []);

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const say = useCallback(async (text) => {
    if (!text) return;
    stop();
    setLoading(true);
    try {
      const res = await fetch(`${API}/voice/say`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(getAuthHeader()?.headers || {}) },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error('voice unavailable');
      const blob = await res.blob();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(blob);
      const audio = new Audio(urlRef.current);
      audioRef.current = audio;
      audio.onplay = () => setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      audio.onpause = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch {
      setSpeaking(false);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, stop]);

  return { say, stop, speaking, loading };
}

export default useNarration;
